import { AlertCircle, CheckCircle, ShieldAlert } from 'lucide-react';
import type { SignatureSummaryData } from './signatureSummary';

import { formatSignatureDate } from './signatureDisplayDate';
const formatTimestamp = (iso: string) => new Date(iso).toLocaleString();

/**
 * Tooltip body for a task's signature state — rendered inside the status pill's
 * mouseover so the signed/awaiting detail no longer needs standalone pills
 * (ROG-2842).
 */
export function SignatureSummaryTooltip({
  summary,
}: {
  summary: SignatureSummaryData;
}) {
  const { complete, signed, awaiting, bypass } = summary;

  if (!signed.length && !awaiting.length && !complete && !bypass) return null;

  return (
    <div className='space-y-1.5'>
      {signed.length > 0 && (
        <ul className='space-y-1'>
          {signed.map(s => (
            <li key={s.id} className='flex items-start gap-1.5'>
              <CheckCircle
                className='h-3.5 w-3.5 mt-0.5 shrink-0'
                aria-hidden='true'
              />
              <span>
                <span className='font-medium'>{s.name}</span>
                {s.roleLabel ? (
                  <span className='ml-1 text-muted-foreground'>
                    ({s.roleLabel})
                  </span>
                ) : null}
                <span className='block text-muted-foreground'>
                  Signed{' '}
                  {formatSignatureDate({
                    signed_at: s.signedAt,
                    signature_date: s.signatureDate,
                  })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* An unqualified "Signed." would be false on a bypassed chart. */}
      {signed.length === 0 && complete && !bypass && <p>Signed.</p>}

      {bypass && (
        <div className='flex items-start gap-1.5 border-t pt-1.5'>
          <ShieldAlert
            className='h-3.5 w-3.5 mt-0.5 shrink-0'
            aria-hidden='true'
          />
          <span>
            <span className='font-medium'>Clinician signature bypassed</span>
            <span className='block text-muted-foreground'>
              {bypass.reasonLabel} — {bypass.actorName}
              {bypass.actorCredentials ? `, ${bypass.actorCredentials}` : ''}
            </span>
            {bypass.comments ? (
              <span className='block text-muted-foreground'>
                {bypass.comments}
              </span>
            ) : null}
            <span className='block text-muted-foreground'>
              {formatTimestamp(bypass.bypassedAt)}
            </span>
          </span>
        </div>
      )}

      {awaiting.map(label => (
        <div key={label} className='flex items-center gap-1.5'>
          <AlertCircle className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
          <span>Awaiting {label}</span>
        </div>
      ))}
    </div>
  );
}
